/** @typedef {import("@honeybadger-io/core").Types.Notice} Notice */

// The frontend does not execute application JavaScript from blob URLs. Keep
// this ownership list in sync if a client feature starts doing so.
const INJECTED_SCRIPT_FRAME_PREFIXES = [
  "chrome-extension:",
  "moz-extension:",
  "safari-extension:",
  "safari-web-extension:",
  "webkit-masked-url:",
  "blob:",
]

const GOOGLE_TAG_MANAGER_SCRIPT_PREFIX =
  "https://www.googletagmanager.com/gtm.js?id=GTM-P3X9S6CC"
const GOOGLE_MAPS_SCRIPT_PREFIX = "https://maps.googleapis.com/"
const ANONYMOUS_FRAME_FILE = "<anonymous>"
const WEBKIT_BRIDGE_MESSAGE =
  "WKWebView API client did not respond to this postMessage"
const BRAVE_STANDARD_SELECTORS_MESSAGE =
  /^TypeError: undefined is not an object \(evaluating '[A-Za-z_$][\w$]*\.standardSelectors'\)$/

// These globals and APIs do not exist in the frontend source or dependency tree.
// Recent examples include Honeybadger faults 123629809, 127511395, and 133143524.
const INJECTED_BROWSER_MESSAGES = [
  "Cannot redefine property: ethereum",
  `can't redefine non-configurable property "ethereum"`,
  "Could not inject ethereum provider because it's not your default extension",
  "undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')",
  "wallet must has at least one account",
  "Invalid call to runtime.sendMessage(). An unknown error occurred.",
  // Safari extension messaging failures (Honeybadger fault 133271410).
  "Error: No Listener: tabs:outgoing.message.ready",
]

const WEGLOT_QUOTA_MESSAGE = "You reached Weglot limitation"

const TURNSTILE_ERROR_NAME = "TurnstileError"
const TURNSTILE_RESET_MESSAGE = "Nothing to reset found"
const TURNSTILE_AUTOMATIC_RETRY_CODE = /\b300\d{3}\b|\b600\d{3}\b/

/**
 * @param {Partial<Notice> | null | undefined} notice
 * @returns {boolean}
 */
function shouldFilterInjectedBrowserNoise(notice) {
  if (typeof window === "undefined") {
    return false
  }

  const injectedFrameIndex = notice?.backtrace?.findIndex(({ file }) =>
    INJECTED_SCRIPT_FRAME_PREFIXES.some(
      (prefix) =>
        typeof file === "string" && file.toLowerCase().startsWith(prefix)
    )
  )

  // Direct extension errors own their first frame. For unhandled rejections,
  // our _app.tsx reporter can precede the injected frame (fault 133275535).
  if (
    injectedFrameIndex === 0 ||
    (injectedFrameIndex > 0 &&
      notice?.context?.unhandledPromiseRejection === true)
  ) {
    return true
  }

  const name = typeof notice?.name === "string" ? notice.name : ""
  const message = typeof notice?.message === "string" ? notice.message : ""

  if (INJECTED_BROWSER_MESSAGES.includes(message)) {
    return true
  }

  // Brave iOS injects its cosmetic-filtering script into page promises. A
  // failed native reply can leave the script without its selector payload
  // (Honeybadger fault 133428798).
  const userAgent =
    typeof globalThis.navigator?.userAgent === "string"
      ? globalThis.navigator.userAgent
      : ""
  if (
    name === "Error" &&
    BRAVE_STANDARD_SELECTORS_MESSAGE.test(message) &&
    notice?.context?.unhandledPromiseRejection === true &&
    /\b(?:iPhone|iPad|iPod)\b/.test(userAgent) &&
    /\bBrave\b/.test(userAgent)
  ) {
    return true
  }

  // Fixinator browser extension background-page failure (133411578).
  if (message.includes("window.fixinatorInputs.has")) {
    return true
  }

  if (
    message.includes("runtime.sendMessage()") &&
    message.includes("Tab not found")
  ) {
    return true
  }

  if (name === "ReferenceError" && message.toLowerCase().includes("jquery")) {
    return true
  }

  return message.includes("window.__firefox__.") || message.includes("zaloJSV2")
}

/**
 * Filters browser errors with signatures that are specific enough to be
 * confidently treated as non-actionable.
 *
 * Keep broad network and permission failures out of this list: those can still
 * point to bugs in application-owned code.
 *
 * @param {Partial<Notice> | null | undefined} notice
 * @returns {boolean}
 */
export function shouldFilterKnownBrowserNoise(notice) {
  const name = typeof notice?.name === "string" ? notice.name : ""
  const message = typeof notice?.message === "string" ? notice.message : ""

  if (shouldFilterInjectedBrowserNoise(notice)) {
    return true
  }

  // A crawler burst triggered this error entirely inside our GTM container
  // (Honeybadger fault 133863118). Keep the signature constrained to a
  // GTM-only stack so application TypeErrors remain observable.
  const hasGtmFrame = notice?.backtrace?.some(
    ({ file }) =>
      typeof file === "string" &&
      file.startsWith(GOOGLE_TAG_MANAGER_SCRIPT_PREFIX)
  )
  const hasOnlyGtmOrAnonymousFrames = notice?.backtrace?.every(
    ({ file }) =>
      !file ||
      file === ANONYMOUS_FRAME_FILE ||
      (typeof file === "string" &&
        file.startsWith(GOOGLE_TAG_MANAGER_SCRIPT_PREFIX))
  )
  if (
    name === "TypeError" &&
    message === "Cannot read properties of null (reading 'includes')" &&
    hasGtmFrame &&
    hasOnlyGtmOrAnonymousFrames
  ) {
    return true
  }

  // Google Maps' dynamic library loader occasionally fails to register an
  // optional module (133995155, 133995156). Suppress only Google-owned stacks.
  const hasOnlyGoogleMapsFrames =
    notice?.backtrace?.length > 0 &&
    notice.backtrace.every(
      ({ file }) =>
        typeof file === "string" && file.startsWith(GOOGLE_MAPS_SCRIPT_PREFIX)
    )
  if (
    name === "Error" &&
    /^Loader\.provide not called by module '[^']+'$/.test(message) &&
    hasOnlyGoogleMapsFrames
  ) {
    return true
  }

  // Injected MutationObservers can fail while crawling unrelated pages. A
  // wholly anonymous stack establishes that no application code owns the
  // observer, regardless of the operation that happened to fail (125120476).
  const hasAnonymousMutationObserverFrame = notice?.backtrace?.some(
    ({ file, method }) =>
      file === ANONYMOUS_FRAME_FILE &&
      typeof method === "string" &&
      method.startsWith("MutationObserver.")
  )
  const hasOnlyAnonymousFrames = notice?.backtrace?.every(
    ({ file }) => file === ANONYMOUS_FRAME_FILE
  )
  if (hasAnonymousMutationObserverFrame && hasOnlyAnonymousFrames) {
    return true
  }

  // HB 132902620 repeats across embedded partners and releases with only
  // anonymous frames, consistent with injected script noise.
  if (
    notice?.context?.embedded === true &&
    name === "SyntaxError" &&
    message ===
      "Failed to execute 'appendChild' on 'Node': Unexpected identifier 'as'" &&
    notice?.backtrace?.length > 0 &&
    hasOnlyAnonymousFrames
  ) {
    return true
  }

  // Storage access denied by the browser (Honeybadger faults 132080490).
  if (
    name === "SecurityError" &&
    [
      "The operation is insecure.",
      "SecurityError: The operation is insecure.",
    ].includes(message)
  ) {
    return true
  }

  // Native WebKit bridge failed to reply in third-party iOS browsers. Some
  // promise wrappers preserve the Error prefix in the message (130576146,
  // 133559591).
  if (name === "Error" && message.includes(WEBKIT_BRIDGE_MESSAGE)) {
    return true
  }

  // ignore spurious Weglot quota messages
  if (message.includes(WEGLOT_QUOTA_MESSAGE)) {
    return true
  }

  // A rejected promise without a reason has no actionable diagnostic (130575503).
  // The custom unhandledrejection listener in src/pages/_app.tsx sets this context flag.
  if (
    message.trim() === "" &&
    notice?.context?.unhandledPromiseRejection === true
  ) {
    return true
  }

  // Firefox can throw this when a browser-owned object is invalidated. The
  // notice has no application stack to act on (132495323).
  if (name === "TypeError" && message === "can't access dead object") {
    return true
  }

  // Safari and Firefox use these exact messages for generic network failures.
  // Only filter notices from our custom unhandledrejection listener so explicit
  // application reporting of the same errors remains visible (132781061, 132789443).
  if (
    name === "TypeError" &&
    ["Load failed", "NetworkError when attempting to fetch resource."].includes(
      message
    ) &&
    notice?.context?.unhandledPromiseRejection === true
  ) {
    return true
  }

  // Canceling the native share sheet is expected user behavior (130593611, 130708207).
  if (
    name === "AbortError" &&
    /share/i.test(message) &&
    /\bcancell?(?:ed|ation)\b/i.test(message)
  ) {
    return true
  }

  // Cloudflare automatically retries the 300*** and 600*** families, while
  // application owners still settle their customer workflows. Suppressing
  // those callback notices prevents retry noise from increasing volume.
  if (
    name === TURNSTILE_ERROR_NAME &&
    TURNSTILE_AUTOMATIC_RETRY_CODE.test(message)
  ) {
    return true
  }

  // The SDK catches failures from imperative reset internally. Keep this
  // already-muted category suppressed so reset handling does not reopen noise.
  if (
    name === TURNSTILE_ERROR_NAME &&
    message.includes(TURNSTILE_RESET_MESSAGE)
  ) {
    return true
  }

  // Turnstile 200100 is a client clock/cache problem and cannot be retried (132540894).
  if (
    name === TURNSTILE_ERROR_NAME &&
    message === "[Cloudflare Turnstile] Error: 200100."
  ) {
    return true
  }

  // AuthLoopDetector deliberately routes clients without the cookie/storage
  // capabilities required for anonymous auth to /check-settings. The notice
  // confirms an unrecoverable browser configuration, not an application fault
  // (133193784, 133479906).
  if (
    name === "Error" &&
    message === "Browser capability unavailable on create-anon"
  ) {
    return true
  }

  return false
}
